import React, { useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Paper,
  useTheme,
} from "@mui/material";
import { motion, useInView } from "framer-motion";
import {
  CheckCircleOutline,
  ArrowForward,
  RocketLaunch,
  Timeline,
  Speed,
  TrackChanges,
  AutoGraph,
  Shield,
  Star,
} from "@mui/icons-material";
import grothImg from "../../assets/groth.webp";
import { useNavigate } from "react-router-dom";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2, delayChildren: 0.3 },
  },
};

const FoundingOfferPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  return (
    <Box sx={{ overflow: "hidden" }}>
      {/* Animated Grid Background */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundImage: `
            linear-gradient(${theme.palette.primary.main}10 1px, transparent 1px),
            linear-gradient(90deg, ${theme.palette.primary.main}10 1px, transparent 1px)
          `,
          backgroundSize: "50px 50px",
          opacity: 0.2,
          zIndex: 0,
          pointerEvents: "none",
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1, py: 8 }}>
        {/* Hero Section */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <Box textAlign="center" mb={6}>
            <motion.div variants={fadeInUp}>
              <Typography
                variant="overline"
                sx={{
                  fontSize: "1rem",
                  color: "primary.main",
                  letterSpacing: 2,
                  fontWeight: 500,
                  mb: 2,
                }}
              >
                [Limited Founding Access]
              </Typography>
            </motion.div>
            <motion.div variants={fadeInUp}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "2.5rem", md: "3rem" },
                  fontWeight: 800,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  mb: 2,
                }}
              >
                The Growth Operating System for Law Firms Under $1M
              </Typography>
            </motion.div>
            <motion.div variants={fadeInUp}>
              <Typography
                variant="h6"
                sx={{ color: "text.secondary", maxWidth: "800px", mx: "auto" }}
              >
                Move from survival and chaos to structured, consistent growth —
                or your money back.
              </Typography>
            </motion.div>
            <motion.div variants={fadeInUp} style={{ marginTop: "2rem" }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: "1.2rem",
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                }}
                onClick={() => navigate("/checkout-50")}
              >
                Apply for Founding Access
              </Button>
            </motion.div>
          </Box>
        </motion.div>

        {/* Value Proposition */}
        <motion.div
          ref={ref}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
        >
          <Grid container spacing={4} alignItems="center" sx={{ my: 4 }}>
            <Grid item xs={12} md={6}>
              <motion.div variants={fadeInUp}>
                <Typography variant="h3" fontWeight={700} gutterBottom>
                  Built by the founder of WLF and IIPLA
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Lexmom combines structured Matter management, workflow
                  control, growth stage clarity, and AI-guided recommendations
                  (rolling out progressively) so you always know what to fix
                  next — without hiring expensive consultants or operating in
                  the dark.
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  We’re opening access to 50 Founding Law Firms before Lexmom
                  transitions to $149/month per user pricing.
                </Typography>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div variants={fadeInUp}>
                <Box
                  component="img"
                  src={grothImg}
                  alt="Growth illustration"
                  sx={{
                    width: "100%",
                    maxWidth: 500,
                    display: "block",
                    mx: "auto",
                  }}
                />
              </motion.div>
            </Grid>
          </Grid>
        </motion.div>

        {/* Problem Section */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          <Paper
            sx={{
              p: { xs: 3, md: 6 },
              my: 8,
              background: "rgba(255,255,255,0.02)",
              backdropFilter: "blur(8px)",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 4,
            }}
          >
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={5}>
                <motion.div variants={fadeInUp}>
                  <Typography variant="h3" fontWeight={700} gutterBottom>
                    Most Law Firms Don’t Fail — They Stall
                  </Typography>
                </motion.div>
              </Grid>
              <Grid item xs={12} md={7}>
                <motion.div variants={fadeInUp}>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    If you’re under $1M, the problem usually isn’t effort. It’s:
                  </Typography>
                  <List>
                    {[
                      "No operational visibility",
                      "No consistent KPI structure",
                      "Founder bottleneck",
                      "Matters and follow-ups slipping through cracks",
                      "Growth decisions based on instinct instead of data",
                      "No clear weekly execution rhythm",
                    ].map((item) => (
                      <ListItem key={item} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleOutline
                            color="primary"
                            fontSize="small"
                          />
                        </ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mt: 2 }}
                  >
                    You’re busy — but not building structured momentum. Lexmom
                    replaces guessing with clarity.
                  </Typography>
                </motion.div>
              </Grid>
            </Grid>
          </Paper>
        </motion.div>

        {/* Timeline: 30 Days / 90 Days */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          <Typography
            variant="h3"
            textAlign="center"
            fontWeight={700}
            gutterBottom
          >
            What You’ll Achieve
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <motion.div variants={fadeInUp}>
                <Card
                  sx={{
                    height: "100%",
                    background: "rgba(255,255,255,0.02)",
                    backdropFilter: "blur(8px)",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                      <Speed
                        sx={{ fontSize: 40, color: "primary.main", mr: 2 }}
                      />
                      <Typography variant="h4" fontWeight={600}>
                        First 30 Days
                      </Typography>
                    </Box>
                    <List>
                      {[
                        "Your firm fully set up inside Lexmom",
                        "Structured Matter workflows so nothing slips through",
                        "Clear stage placement (Foundation or Stability)",
                        "Defined core metrics to monitor",
                        "A weekly operating rhythm for owner review",
                      ].map((item) => (
                        <ListItem key={item} sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircleOutline
                              color="primary"
                              fontSize="small"
                            />
                          </ListItemIcon>
                          <ListItemText primary={item} />
                        </ListItem>
                      ))}
                    </List>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 2 }}
                    >
                      You move from reactive chaos to structured execution.
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div variants={fadeInUp}>
                <Card
                  sx={{
                    height: "100%",
                    background: "rgba(255,255,255,0.02)",
                    backdropFilter: "blur(8px)",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                      <AutoGraph
                        sx={{ fontSize: 40, color: "secondary.main", mr: 2 }}
                      />
                      <Typography variant="h4" fontWeight={600}>
                        Over the Next 90 Days
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      paragraph
                    >
                      As AI Managers roll out progressively, you’ll begin
                      receiving:
                    </Typography>
                    <List>
                      {[
                        "Stage-specific recommendations",
                        "Bottleneck alerts",
                        "Workflow improvement prompts",
                        "Operational focus suggestions",
                        "Weekly execution guidance",
                      ].map((item) => (
                        <ListItem key={item} sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <Star color="secondary" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={item} />
                        </ListItem>
                      ))}
                    </List>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 2 }}
                    >
                      AI recommendations are advisory. You remain the
                      decision-maker.
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </motion.div>

        {/* How It Works */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
          style={{ marginTop: "6rem" }}
        >
          <Typography
            variant="h3"
            textAlign="center"
            fontWeight={700}
            gutterBottom
          >
            How It Works
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            {[
              {
                title: "Diagnose Your Growth Stage",
                desc: "Identify whether you’re in Foundation or Stability and uncover what’s holding you back.",
                icon: <TrackChanges fontSize="large" />,
              },
              {
                title: "Implement Structured Workflow",
                desc: "Lexmom becomes your central operating system: pipeline visibility, Matter progression control, task accountability, workflow consistency.",
                icon: <RocketLaunch fontSize="large" />,
              },
              {
                title: "Track, Adjust, Advance",
                desc: "Use structured metrics and AI prompts to reduce chaos, improve discipline, and move toward your next stage.",
                icon: <Timeline fontSize="large" />,
              },
            ].map((step) => (
              <Grid item xs={12} md={4} key={step.title}>
                <motion.div variants={fadeInUp}>
                  <Card
                    sx={{
                      textAlign: "center",
                      p: 3,
                      background: "rgba(255,255,255,0.02)",
                      backdropFilter: "blur(8px)",
                      border: "1px solid",
                      borderColor: "divider",
                      height: "100%",
                    }}
                  >
                    <Box sx={{ color: "primary.main", mb: 2 }}>{step.icon}</Box>
                    <Typography variant="h5" fontWeight={600} gutterBottom>
                      {step.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {step.desc}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>

        {/* Founding 50 Access Includes */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
          style={{ marginTop: "6rem" }}
        >
          <Paper
            sx={{
              p: { xs: 3, md: 5 },
              background: `linear-gradient(145deg, ${theme.palette.primary.dark}20, ${theme.palette.secondary.dark}20)`,
              backdropFilter: "blur(8px)",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 4,
            }}
          >
            <Typography variant="h3" fontWeight={700} gutterBottom>
              Founding 50 Law Firm Growth Partner Access Includes
            </Typography>
            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <List>
                  {[
                    "Full Lexmom Matter Management Platform",
                    "Structured Workflow System",
                    "7-Stage Law Firm Growth Framework",
                    "Stage Placement Assessment",
                    "All Future AI Managers Included (as they roll out)",
                    "Lifetime Access (never offered again)",
                  ].map((item) => (
                    <ListItem key={item}>
                      <ListItemIcon>
                        <CheckCircleOutline color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <List>
                  {[
                    "Protection from future $149/month per user pricing",
                    "Private Founders Circle (feedback + roadmap input)",
                    "Priority onboarding support",
                    "Bonus: AI Implementation Playbook (PDF)",
                  ].map((item) => (
                    <ListItem key={item}>
                      <ListItemIcon>
                        <CheckCircleOutline color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={item} />
                    </ListItem>
                  ))}
                </List>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  The Playbook includes: KPI setup walkthrough, growth stage
                  advancement checklist, 90-day execution roadmap, weekly
                  operating rhythm template.
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </motion.div>

        {/* What's Available Now vs Rolling Out */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
          style={{ marginTop: "6rem" }}
        >
          <Typography
            variant="h3"
            textAlign="center"
            fontWeight={700}
            gutterBottom
          >
            What’s Available Today vs What’s Rolling Out
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <motion.div variants={fadeInUp}>
                <Card
                  sx={{
                    p: 3,
                    background: "rgba(255,255,255,0.02)",
                    backdropFilter: "blur(8px)",
                    border: "1px solid",
                    borderColor: "divider",
                    height: "100%",
                  }}
                >
                  <Typography variant="h5" color="primary.main" gutterBottom>
                    Available Now
                  </Typography>
                  <List>
                    {[
                      "Matter management",
                      "Matter tracking",
                      "Workflow structure",
                      "Task management",
                      "Foundational operational organization",
                    ].map((item) => (
                      <ListItem key={item}>
                        <ListItemIcon>
                          <CheckCircleOutline color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </Card>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div variants={fadeInUp}>
                <Card
                  sx={{
                    p: 3,
                    background: "rgba(255,255,255,0.02)",
                    backdropFilter: "blur(8px)",
                    border: "1px solid",
                    borderColor: "divider",
                    height: "100%",
                  }}
                >
                  <Typography variant="h5" color="secondary.main" gutterBottom>
                    Rolling Out Progressively
                  </Typography>
                  <List>
                    {[
                      "AI Manager modules",
                      "KPI dashboard enhancements",
                      "Bottleneck detection logic",
                      "Growth stage automation prompts",
                    ].map((item) => (
                      <ListItem key={item}>
                        <ListItemIcon>
                          <Star color="secondary" />
                        </ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 2 }}
                  >
                    Founding Partners receive all AI modules at no additional
                    cost as they launch.
                  </Typography>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </motion.div>

        {/* Pricing */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
          style={{ marginTop: "6rem" }}
        >
          <Paper
            sx={{
              p: { xs: 3, md: 5 },
              textAlign: "center",
              background: `linear-gradient(145deg, ${theme.palette.primary.dark}30, ${theme.palette.secondary.dark}30)`,
              backdropFilter: "blur(8px)",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 4,
            }}
          >
            <Typography variant="h2" fontWeight={800} gutterBottom>
              $997
            </Typography>
            <Typography variant="h5" color="text.secondary" gutterBottom>
              one-time • Founding 50 Lifetime (20 Years) Access
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Includes up to 3 users per firm. Additional users can be added
              later at standard pricing.
            </Typography>
            <Divider sx={{ my: 3 }} />
            <Typography variant="body1" color="text.secondary">
              Future public pricing: <strong>$149/month per user</strong> =
              $1,788/year per user
            </Typography>
            <Typography variant="body1" color="primary.main" sx={{ mt: 1 }}>
              Founding Partners break even in under 7 months.
            </Typography>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              sx={{ mt: 4, px: 5, py: 1.5, fontSize: "1.2rem" }}
              onClick={() => navigate("/checkout-50")}
            >
              Apply for Founding Access
            </Button>
          </Paper>
        </motion.div>

        {/* Guarantee */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeInUp}
          style={{ marginTop: "6rem", textAlign: "center" }}
        >
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
            <Shield sx={{ fontSize: 60, color: "primary.main" }} />
            <Typography variant="h4" fontWeight={600}>
              30-Day Founding Partner Setup Guarantee
            </Typography>
          </Box>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: "800px", mx: "auto", mt: 2 }}
          >
            If we can’t get your firm fully set up and using Lexmom as your
            central Matter workflow within 30 days — with clear stage placement
            and operating structure — we’ll personally troubleshoot with you. If
            after that strategy call you still don’t feel confident in the
            system, we’ll refund 100% of your investment.
          </Typography>
        </motion.div>

        {/* Limited Availability */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeInUp}
          style={{ marginTop: "6rem", textAlign: "center" }}
        >
          <Typography variant="h3" fontWeight={700} gutterBottom>
            Founding Access Is Limited
          </Typography>
          <Typography variant="h5" color="primary.main" gutterBottom>
            Only 50 firms
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: "700px", mx: "auto" }}
          >
            Lifetime access will never be offered again. Once filled, Lexmom
            moves permanently to monthly pricing. This offer will not reopen. We
            are selecting firms committed to structured growth.
          </Typography>
        </motion.div>

        {/* This Is For You If */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
          style={{ marginTop: "6rem" }}
        >
          <Paper
            sx={{ p: { xs: 3, md: 5 }, background: "rgba(255,255,255,0.02)" }}
          >
            <Typography variant="h3" fontWeight={700} gutterBottom>
              This Is For You If
            </Typography>
            <Grid container spacing={2}>
              {[
                "You are under $1M in annual revenue",
                "You want operational structure and clarity",
                "You are willing to implement systems",
                "You understand AI is advisory, not autonomous",
              ].map((item) => (
                <Grid item xs={12} md={6} key={item}>
                  <Box display="flex" alignItems="center">
                    <CheckCircleOutline color="primary" sx={{ mr: 1 }} />
                    <Typography variant="body1">{item}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              This is not for firms looking for shortcuts or passive tools.
            </Typography>
          </Paper>
        </motion.div>

        {/* FAQ */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
          style={{ marginTop: "6rem" }}
        >
          <Typography
            variant="h3"
            textAlign="center"
            fontWeight={700}
            gutterBottom
          >
            Frequently Asked Questions
          </Typography>
          <Grid container spacing={3} sx={{ mt: 2 }}>
            {[
              {
                q: "Is Lexmom usable today?",
                a: "Yes. The platform is live and fully usable for Matter management and workflow control. AI modules are rolling out progressively.",
              },
              {
                q: "Do you guarantee AI accuracy?",
                a: "No. AI recommendations are advisory. You make final decisions. We guarantee structured setup and operational adoption — or your money back.",
              },
              {
                q: "How many users are included?",
                a: "Founding Lifetime (20 Years) includes up to 3 users per firm.",
              },
              {
                q: "What happens after 50 firms?",
                a: "This lifetime offer closes permanently. Future access will be $149/month per user.",
              },
              {
                q: "How fast can we start?",
                a: "Immediately after acceptance. Founding Partners receive priority onboarding.",
              },
            ].map((faq) => (
              <Grid item xs={12} md={6} key={faq.q}>
                <motion.div variants={fadeInUp}>
                  <Card
                    sx={{
                      p: 3,
                      background: "rgba(255,255,255,0.02)",
                      backdropFilter: "blur(8px)",
                      border: "1px solid",
                      borderColor: "divider",
                      height: "100%",
                    }}
                  >
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {faq.q}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {faq.a}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>

        {/* Final CTA */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeInUp}
          style={{ marginTop: "6rem", textAlign: "center" }}
          id="apply"
        >
          <Typography variant="h3" fontWeight={700} gutterBottom>
            Become One of the 50 Founding Growth Firms
          </Typography>
          <Typography variant="h5" color="text.secondary" sx={{ mb: 4 }}>
            Move from reactive chaos to structured, consistent growth.
          </Typography>
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForward />}
            sx={{ px: 5, py: 1.5, fontSize: "1.2rem" }}
            onClick={() => navigate("/checkout-50")}
          >
            Apply for Founding Access Now
          </Button>
        </motion.div>
      </Container>
    </Box>
  );
};

export default FoundingOfferPage;
